import type { FC } from 'react';
import React, { memo, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FlagsProvider } from '@atlaskit/flag';

import { SPACE_OVERVIEW } from '@confluence/named-routes';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon';
import { SpaceStarButton } from '@confluence/action-buttons/entry-points/spaceStar';
import { SpaceWatchButton } from '@confluence/action-buttons/entry-points/spaceWatch';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { useSessionData } from '@confluence/session-data';

import type { SpaceProp, ModifyStarProp, ResponsiveCategoriesProps } from '../types';

import { Categories } from './Categories';
import { Menu } from './Menu';
import {
	Row,
	Title,
	SpaceLink,
	IconContainer,
	IconPlaceholder,
	SpaceItem,
} from './PresentationalComponents';

export const DEFAULT_ICON_PATH = '/images/logo/default-space-logo-256.png';

type AnalyticsProps = {
	ranking: number;
	pageNumber: number;
	textFilter: boolean;
};

type TableRowProps = SpaceProp &
	ModifyStarProp &
	AnalyticsProps &
	ResponsiveCategoriesProps & {
		isSpaceAliasFFEnabled: boolean;
	};

export const TableRow: FC<TableRowProps> = memo(
	({
		space,
		modifyStarCache,
		ranking,
		pageNumber,
		textFilter,
		categoriesWidth,
		setCategoriesWidth,
		isSpaceAliasFFEnabled = false,
	}) => {
		const { isLicensed } = useSessionData();
		const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();
		const iconPath = `${space.icon?.path || DEFAULT_ICON_PATH}`;

		const { createAnalyticsEvent } = useAnalyticsEvents();

		const onClick = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'spaceLink',
					source: 'spaceDirectoryScreen',
					attributes: {
						spaceId: space.id,
						ranking,
						pageNumber,
						textFilter,
						isGuest,
					},
				},
			}).fire();
		}, [space.id, ranking, pageNumber, textFilter, createAnalyticsEvent, isGuest]);

		const analytics = {
			source: 'spaceDirectoryScreen',
			attributes: {
				spaceId: space.id,
				isGuest,
			},
		};

		const SpaceItemContent = () => (
			<>
				<SpaceIcon src={iconPath} testId="space-icon" label="" />
				<Title title={space.name || ''}>{space.name}</Title>
				<Categories
					space={space}
					categoriesWidth={categoriesWidth}
					setCategoriesWidth={setCategoriesWidth}
				/>
				<IconPlaceholder />
			</>
		);

		return (
			<FlagsProvider>
				<Row>
					<SpaceItem>
						<SpaceLink
							href={SPACE_OVERVIEW.toUrl({
								spaceKey: (isSpaceAliasFFEnabled && space.alias) || space.key,
							})}
							onClick={onClick}
							tabIndex={0}
						>
							<SpaceItemContent />
						</SpaceLink>
					</SpaceItem>
					{isLicensed ? (
						<IconContainer>
							<SpaceWatchButton
								spaceId={space.id!}
								isWatched={space.currentUser.isWatched}
								analytics={analytics}
							/>
							<SpaceStarButton
								spaceId={space.id!}
								spaceKey={space.key!}
								isStarred={space.currentUser.isFavourited}
								modifyStarCache={modifyStarCache}
								analytics={analytics}
							/>
							<Menu space={space} modifyStarCache={modifyStarCache} />
						</IconContainer>
					) : null}
				</Row>
			</FlagsProvider>
		);
	},
);
